import "./ProductFeaturesSection.css";
import { ProductFeatureRow } from "@tennr/frontend/components/ProductFeatureRow";
import { ProductFeatureUnderTheHood } from "@tennr/frontend/components/ProductFeatureUnderTheHood";

// define the under the hood content for each feature
const underTheHoodContent = {
  1: {
    paragraphs: [
      "Tennr uses an ensemble of proprietary algorithms to determine what type of medical document has been received—from CRs to CMNs to PAPs to ABNs—all with 99.9% accuracy. Other systems use traditional AI, like computer vision, to determine what document type something is. But Tennr's document-type algorithms have been optimized for the messy documents in medical referrals—some of which contain handwritten scribbles and 20-page faxes.",
      "Our page-splitter then separates out each individual document in that 20-page fax based on contextual and visual clues. At this point, Tennr automatically assigns documents to the right patients and orders—even if the information doesn't exactly match—using Tennr's Levenshtein fuzzy matching algorithm.",
    ],
    // imageSrc: "/img/product-under-the-hood-1.svg",
  },
  2: {
    paragraphs: [
      "Every eligibility check is wrong 8% of the time. So, Tennr algorithmically determines what questions need to be asked to confirm the information.",
      "RaeLM™ extracts every data point possible—prioritizing key things like member IDs and subscriber dates—from every type of medical document, whether it's a PDF, image, or fax. RaeLM is specifically trained on medical documents, so it understands the relationships between a patient and a subscriber, the various ways someone might spell Aetna, and so on. RaeLM looks like an out-of-the-box LLM, but it's actually been purpose built for the administrative side of medical care.",
    ],
    // imageSrc: "/img/product-under-the-hood-2.svg",
  },
  3: {
    paragraphs: [
      "Traditional LLMs don't work for medical phone calls. Why? Because these medical phone calls are 30-45 minutes of someone being on hold, and someone reading member IDs five times. Tennr's T3 system (Transcript Translation Technology) translates medical call transcripts into reliable data. By combining heuristic, deterministic, and probabilistic models, T3 can understand the unique patterns and cadences of medical benefit calls.",
    ],
    // imageSrc: "/img/product-under-the-hood-3.svg",
  },
  4: {
    paragraphs: [
      'Tennr\'s Health Code Extractor™ (THCE) identifies narratives and maps them to a list of codes, from ICDs to HCPCS and CPTs. In many cases, medical staff detail a patient\'s needs through shorthand "narratives," and humans need to "map" those descriptions to the codes that are expected for successful billing cycles. THCE knows what description or narrative corresponds to what code instantly. And yes, we call it THCE because we thought you\'d like another acronym.',
      "Our Qualifications Model (QLM™ ), uses data extracted by RaeLM™ and payor guidelines across thousands of CPT & HCPCS codes. QLM™ assesses the adherence to criteria across all documents pertaining to the patient. It turns blobs of data into structured information that's readable and audit-ready.",
    ],
    // imageSrc: "/img/product-under-the-hood-4.svg",
  },
  5: {
    paragraphs: [
      "Similarly to dealing with insurance, this isn't a machine-learning problem—rather, it's a systems problem. At this point, we have all the right information, we know what we're missing, and where we need to go and get it. Instead, this step is about making sure the right automations are firing to go and get that data—whether it's collecting an insurance card from a patient or getting a certificate of medical necessity signed by a referring provider.",
      "We've configured Tennr to know how to automatically procure that information and stop immediately once the job is done (regardless of whether it came through a document or phone call).",
    ],
    // imageSrc: "/img/product-under-the-hood-5.svg",
  },
};

// define the features data
const features = [
  {
    id: 1,
    title: "THE UNIVERSAL INBOX",
    heading: "Document wrangling,\n classification, and triage",
    body: "Tennr wrangles, tags, and triages every document. Nothing piles up or falls through the cracks—whether there are 10 patients on one document, or 10 documents for a single patient.",
    ctaText: "UNDER THE HOOD",
    lottieSrc: "/lottie/tennr_productpage_wrangling.lottie",
    imageAlt: "left",
    expandedContent: (
      <ProductFeatureUnderTheHood
        title="THE UNIVERSAL INBOX"
        heading="Document wrangling, classification, and triage"
        body="Tennr wrangles, tags, and triages every document. Nothing piles up or falls through the cracks—whether there are 10 patients on one document, or 10 documents for a single patient."
        content={underTheHoodContent[1]}
      />
    ),
  },
  {
    id: 2,
    title: "INTAKE & ELIGIBILITY ",
    heading: "Intake with real speed\nand accuracy ",
    body: "Perform key functions in minutes. From payor selection and code extraction to order determinations and patient-matching, we give you clean data in your EHR and to share with referrers, patients, and reps.",
    ctaText: "UNDER THE HOOD",
    imageAlt: "right",
    lottieSrc: "/lottie/PDP_intake.lottie",
    expandedContent: (
      <ProductFeatureUnderTheHood
        title="INTAKE & ELIGIBILITY"
        heading="Intake with real speed and accuracy"
        body="Perform key functions in minutes. From payor selection and code extraction to order determinations and patient-matching, we give you clean data in your EHR and to share with referrers, patients, and reps."
        content={underTheHoodContent[2]}
      />
    ),
  },
  {
    id: 3,
    title: "BENEFITS INVESTIGATION",
    heading: "Complex benefits investigations, simplified ",
    body: "Tennr verifies benefits coverage digitally or with comprehensive phone calls. All you have to do is review the information. Don't spend 45 minutes on hold, we'll handle it.",
    ctaText: "UNDER THE HOOD",
    lottieSrc: "/lottie/tennr_productpage_benefittitle.lottie",
    imageAlt: "left",
    expandedContent: (
      <ProductFeatureUnderTheHood
        title="BENEFITS INVESTIGATION"
        heading="Complex benefits investigations, simplified"
        body="Tennr verifies benefits coverage digitally or with comprehensive phone calls. All you have to do is review the information. Don't spend 45 minutes on hold, we'll handle it."
        content={underTheHoodContent[3]}
      />
    ),
  },
  {
    id: 4,
    title: "QUALIFICATION",
    heading: "Clinical qualification\nmade easy",
    body: "Tennr ensures correct documentation from referrals. Identify payor and plan types, spot errors, and approve whether you have the right info or not.\n\nIf something's missing, easily request the information from providers. Never forget auth-specific criteria again.",
    ctaText: "UNDER THE HOOD",
    imageAlt: "right",
    lottieSrc: "/lottie/PDP_clinical_text.lottie",
    expandedContent: (
      <ProductFeatureUnderTheHood
        title="QUALIFICATION"
        heading="Clinical qualification made easy"
        body={
          "Tennr ensures correct documentation from referrals. Identify payor and plan types, spot errors, and approve whether you have the right info or not.\n\nIf something's missing, easily request the information from providers. Never forget auth-specific criteria again."
        }
        content={underTheHoodContent[4]}
      />
    ),
  },
  {
    id: 5,
    title: "COMMUNICATION",
    heading: "Visibility for providers and patients",
    body: "Tennr handles all information requests and status updates—keeping referrers, patients, and payors in the loop at all times.",
    ctaText: "UNDER THE HOOD",
    lottieSrc: "/lottie/tennr_productpage_visibility.lottie",
    imageAlt: "left",
    expandedContent: (
      <ProductFeatureUnderTheHood
        title="COMMUNICATION"
        heading="Visibility for providers and patients"
        body="Tennr handles all information requests and status updates—keeping referrers, patients, and payors in the loop at all times."
        content={underTheHoodContent[5]}
      />
    ),
  },
];

export const ProductFeaturesSection = () => {
  return (
    <section className="product-features-section">
      <div className="product-features-container">
        {features.map((feature, index) => (
          <ProductFeatureRow key={feature.id} feature={feature} isReversed={index % 2 === 1} index={index} />
        ))}
      </div>
    </section>
  );
};
